import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, Users, Eye, Globe, ArrowUpRight, 
  Calendar, TrendingUp, Monitor, Smartphone,
  RefreshCw
} from 'lucide-react';

export default function AdminAnalytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics/stats?days=${days}`);
      const text = await response.text();
      const data = JSON.parse(text);
      setStats(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [days]);

  const StatCard = ({ icon: Icon, label, value, subtext, color = '#6366f1' }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '24px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={20} color={color} />
        </div>
        <ArrowUpRight size={16} style={{ opacity: 0.3 }} />
      </div>
      <div style={{ fontSize: '32px', fontWeight: '600', color: '#fff', marginBottom: '4px' }}>
        {value}
      </div>
      <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>{label}</div>
      {subtext && (
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginTop: '8px' }}>{subtext}</div>
      )}
    </motion.div>
  );

  const BarChartSimple = ({ data, label }) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: 'rgba(255,255,255,0.3)',
          fontSize: '14px' 
        }}>
          Ingen data ennå
        </div>
      );
    }
    
    const maxValue = Math.max(...Object.values(data));
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Object.entries(data).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '100px', 
              fontSize: '12px', 
              color: 'rgba(255,255,255,0.5)',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}>
              {key}
            </div>
            <div style={{ flex: 1, height: '24px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', overflow: 'hidden' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(value / maxValue) * 100}%` }}
                transition={{ duration: 0.5 }}
                style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                  borderRadius: '4px',
                }}
              />
            </div>
            <div style={{ width: '40px', fontSize: '13px', color: '#fff', textAlign: 'right', fontWeight: '500' }}>
              {value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      color: '#fff',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        padding: '24px 32px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '500', marginBottom: '4px' }}>
            📊 Analytics Dashboard
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)' }}>
            Trafikk og besøksstatistikk
          </p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Period selector */}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '10px 16px',
              color: '#fff',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value={1}>Siste 24 timer</option>
            <option value={7}>Siste 7 dager</option>
            <option value={30}>Siste 30 dager</option>
            <option value={90}>Siste 90 dager</option>
          </select>
          
          <button
            onClick={fetchStats}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '10px 16px',
              color: '#fff',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Oppdater
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
        {loading && !stats ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.4)' }}>
            Laster analytics...
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '32px',
            }}>
              <StatCard
                icon={Eye}
                label="Sidevisninger"
                value={stats?.total_pageviews || 0}
                subtext={`Siste ${days} dager`}
                color="#6366f1"
              />
              <StatCard
                icon={Users}
                label="Unike besøkende"
                value={stats?.unique_visitors || 0}
                subtext="Estimert"
                color="#22c55e"
              />
              <StatCard
                icon={TrendingUp}
                label="Snitt per dag"
                value={stats?.total_pageviews ? Math.round(stats.total_pageviews / days) : 0}
                color="#f59e0b"
              />
              <StatCard
                icon={Globe}
                label="Kilder"
                value={stats?.sources ? Object.keys(stats.sources).length : 0}
                color="#ec4899"
              />
            </div>

            {/* Charts Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '24px',
            }}>
              {/* Daily views */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px',
                  padding: '24px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <Calendar size={18} style={{ opacity: 0.5 }} />
                  <h3 style={{ fontSize: '16px', fontWeight: '500' }}>Sidevisninger per dag</h3>
                </div>
                <BarChartSimple data={stats?.daily} />
              </motion.div>

              {/* Sources */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px',
                  padding: '24px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <Globe size={18} style={{ opacity: 0.5 }} />
                  <h3 style={{ fontSize: '16px', fontWeight: '500' }}>Trafikkkilder</h3>
                </div>
                <BarChartSimple data={stats?.sources} />
              </motion.div>

              {/* Top pages */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px',
                  padding: '24px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <BarChart3 size={18} style={{ opacity: 0.5 }} />
                  <h3 style={{ fontSize: '16px', fontWeight: '500' }}>Populære sider</h3>
                </div>
                <BarChartSimple data={stats?.pages} />
              </motion.div>

              {/* Referrers */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px',
                  padding: '24px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <ArrowUpRight size={18} style={{ opacity: 0.5 }} />
                  <h3 style={{ fontSize: '16px', fontWeight: '500' }}>Eksterne henvisninger</h3>
                </div>
                <BarChartSimple data={stats?.referrers} />
              </motion.div>
            </div>

            {/* Integration Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              style={{
                marginTop: '32px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '16px',
                padding: '24px',
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: '500', marginBottom: '20px' }}>
                🔗 Aktive integrasjoner
              </h3>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {[
                  { name: 'Google Analytics 4', id: 'G-VV2F9PKVZQ', color: '#f59e0b' },
                  { name: 'Meta Pixel', id: '1916199428986463', color: '#3b82f6' },
                  { name: 'LinkedIn Insight Tag', id: '8517018', color: '#0077b5' },
                ].map((integration) => (
                  <div
                    key={integration.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '8px',
                      border: `1px solid ${integration.color}30`,
                    }}
                  >
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#22c55e',
                    }} />
                    <span style={{ fontSize: '13px', color: '#fff' }}>{integration.name}</span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{integration.id}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
